import type { OutgoingMessage, IncomingMessage } from '@/types';

interface EmbedConfig {
  partner: string;
  theme: 'light' | 'dark';
  locale: string;
  featureFlags: Record<string, boolean>;
}

const ALLOWED_ORIGINS = [
  'https://cabinetvision-web.pages.dev',
  'https://cabinetvision-web.vercel.app',
  'https://cabinetvision.com',
  'https://www.cabinetvision.com',
  'http://localhost:5173',
  'http://localhost:3000',
];

let hostOrigin: string | null = null;
const messageQueue: { type: OutgoingMessage['type']; payload?: unknown }[] = [];

let messageListenerActive = false;

// ── Host viewport tracking ─────────────────────────────────────────────────
// The app lives inside an iframe, so `window.innerHeight` is the iframe's own
// (driven) height — useless for sizing viewport-filling screens. The host page
// reports its real viewport height via INIT / HOST_RESIZE so screens like the
// Preview canvas can adapt to the actual device.
let hostHeight: number | null = null;
const hostHeightListeners = new Set<(h: number) => void>();

export function getHostHeight(): number | null {
  return hostHeight;
}

export function onHostHeightChange(cb: (h: number) => void): () => void {
  hostHeightListeners.add(cb);
  return () => hostHeightListeners.delete(cb);
}

function setHostHeight(value: unknown) {
  if (typeof value === 'number' && value > 0 && value < 10000) {
    hostHeight = value;
    hostHeightListeners.forEach((cb) => cb(value));
  }
}

export function initPostMessage() {
  if (typeof window === 'undefined') return;
  if (messageListenerActive) return;

  window.addEventListener('message', handleIncomingMessage);
  messageListenerActive = true;

  // Send READY once the app is loaded
  sendMessage('READY', {
    version: '1.0.0',
    features: ['webgpu', 'camera', 'save'],
    deviceTier: detectDeviceTier(),
  });
}

export function cleanupPostMessage() {
  if (typeof window === 'undefined') return;
  if (!messageListenerActive) return;

  window.removeEventListener('message', handleIncomingMessage);
  messageListenerActive = false;
}

function detectDeviceTier() {
  const hasWebGPU = typeof navigator !== 'undefined' && 'gpu' in navigator;

  if (hasWebGPU) return 'A';
  return 'B';
}

export function sendMessage(type: OutgoingMessage['type'], payload?: unknown) {
  if (typeof window === 'undefined') return;

  // Validate we are actually inside an iframe before sending to parent
  if (window.self === window.top) return;

  if (!hostOrigin) {
    // Queue outbound messages until we know the validated host origin
    messageQueue.push({ type, payload });
    return;
  }

  window.parent.postMessage({ type, payload }, hostOrigin);
}

function flushMessageQueue() {
  if (!hostOrigin) return;
  while (messageQueue.length > 0) {
    const msg = messageQueue.shift()!;
    window.parent.postMessage({ type: msg.type, payload: msg.payload }, hostOrigin);
  }
}

function handleIncomingMessage(event: MessageEvent<IncomingMessage>) {
  // Only accept messages from the parent window
  if (event.source !== window.parent) {
    return;
  }

  // Validate origin against whitelist
  if (!ALLOWED_ORIGINS.includes(event.origin)) {
    return;
  }

  const wasFirstOrigin = !hostOrigin;
  hostOrigin = event.origin;

  // Flush any queued messages now that we have a validated origin
  if (wasFirstOrigin) {
    flushMessageQueue();
  }

  const { type, payload } = event.data;

  switch (type) {
    case 'INIT':
      setHostHeight((payload as { hostHeight?: unknown } | undefined)?.hostHeight);
      handleInit(payload as EmbedConfig);
      break;
    case 'HOST_RESIZE':
      setHostHeight((payload as { hostHeight?: unknown } | undefined)?.hostHeight);
      break;
    case 'SET_CONFIG':
      handleSetConfig(payload as Partial<EmbedConfig>);
      break;
    case 'OPEN':
      // Visualizer is already open in iframe
      break;
    case 'CLOSE':
      sendMessage('CLOSED');
      break;
    case 'SET_FINISH':
      // TODO: integrate with store
      break;
    case 'RESET':
      // TODO: integrate with store reset
      break;
    default:
      console.warn('Unknown incoming message type:', type);
  }
}

function handleInit(config: EmbedConfig) {
  console.log('Visualizer initialized with config:', config);
  sendMessage('LOADED');
}

function handleSetConfig(config: Partial<EmbedConfig>) {
  console.log('Config updated:', config);
}

export function requestResize(height: number) {
  sendMessage('RESIZE_REQUEST', { height });
}

export function reportError(code: string, message: string, recoverable: boolean) {
  sendMessage('ERROR', { code, message, recoverable });
}

export function reportQuoteRequested(data: unknown) {
  sendMessage('QUOTE_REQUESTED', data);
}

export function reportFinishSelected(finishId: string, glossLevel: string) {
  sendMessage('FINISH_SELECTED', { finishId, glossLevel });
}
